import type { GameId } from "@/constants/GAMES"
export type MarkerId =
  | "turtle"
  | "catfish"
  | "beluga"
  | "iniya"
  | "neons"
  | "stingray"
  | "blueTang"
  | "octopus"
  | "whale"

export const MARKER_ORDER: MarkerId[] = [
  "stingray",
  "beluga",
  "catfish",
  "neons",
  "turtle",
  "iniya",
  "blueTang",
  "octopus",
  "whale",
]

export function getMarkerMissionNumber(markerId: MarkerId): number {
  return MARKER_ORDER.indexOf(markerId) + 1
}

type MarkerTheme = {
  accent: string
  panel: string
}

export type MarkerFact = {
  shortTitle: string
  text: string
  image: string
  second?: boolean
}

export type FinalWordLetter = "Э" | "К" | "О" | "Л" | "Г" | "И" | "А"

export type MarkerHint = {
  mascot: string
  text: string
}

export type MarkerVideoSource = {
  src: string
  type: string
}

export type MarkerVideoAsset = {
  poster?: string
  sources: MarkerVideoSource[]
  variants?: Partial<Record<"high", {
    poster?: string
    sources: MarkerVideoSource[]
  }>>
  fitToWidth?: boolean
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down"
  objectPosition?: string
}

export type MarkerVideos = {
  welcome?: MarkerVideoAsset
  fact?: MarkerVideoAsset
  thankYou?: MarkerVideoAsset
}

export type MarkerConfig = {
  id: MarkerId
  name: string
  character: string
  greeting: string
  theme: MarkerTheme
  code: string
  gameId: GameId | null
  letter: FinalWordLetter
  thankYouText: string
  thankYouCharacter: string
  mascotMini: string
  thankYouVideo?: string
  videos?: MarkerVideos
  hint: MarkerHint | null
  factShortTitle: string
  factText: string
  factImage: string
  second?: boolean
}

const MARKER_SECOND_FACTS: Record<MarkerId, MarkerFact> = {
  turtle: {
    shortTitle: "используй меньше пластика",
    text: "Откажись от одноразовых пластиковых пакетов, когда идешь за покупками! Океану и черепахам будет лучше, если использовать многоразовую сумку-шопер. А еще выбирай молоко не в картонном тетрапаке, а в пластиковой бутылке 1 PET (ее легче переработать), и бери йогурт в бутылке без пленочной этикетки.",
    image: "/img/facts/turtle-2.png",
    second: true,
  },
  catfish: {
    shortTitle: "Меньше химии в еде!",
    text: "Выбирая овощи и фрукты, берите сезонные, выращенные местными фермерами или в экологичных агрокомплексах. Так своим выбором мы покажем производителям, что необходимо отказаться от использования опасных химикатов и пестицидов.",
    image: "/img/facts/catfish-2.png",
    second: true,
  },
  beluga: {
    shortTitle: "мой транспорт на специальных площадках",
    text: "Не сливай загрязненную воду в открытые водоемы. Авто- и мототранспорт, а также велосипеды и прочую технику нужно мыть только на специализированных площадках, где осуществляется очистка сточных вод от нефтепродуктов, взвешенных частиц и прочих загрязнений.",
    image: "/img/facts/beluga-2.jpg",
    second: true,
  },
  iniya: {
    shortTitle: "пользуйся эко-средствами в быту",
    text: "Переходи на бытовые средства с экосоставом. Используй упаковку из полиэтилена высокой плотности. Такая тара имеет характерный продольный шов на дне, она является ценным вторсырьем с большими возможностями для переработки.",
    image: "/img/facts/iniya-2.png",
    second: true,
  },
  neons: {
    shortTitle: "ешь из многоразовой посуды",
    text: "Откажись от одноразовой пластиковой посуды и столовых приборов. Если все же избежать этого не удалось, сдай в переработку пластиковую посуду и другие отходы.",
    image: "/img/facts/neons-2.jpg",
    second: true,
  },
  stingray: {
    shortTitle: "прибирай за собой на пляже",
    text: "Не оставляй пакеты на пляже и не выбрасывай их. Ветер может унести пакет в воду, от воздействия воды и солнца пакет разложится на мелкие частицы, и скат по ошибке может его съесть. Лучше пользоваться многоразовой сумкой.",
    image: "/img/facts/stingray-2.jpg",
    second: true,
  },
  blueTang: {
    shortTitle: "выбирай безопасный крем от загара",
    text: "Пользуйся кремом от загара с пометкой «Безопасный для рифов», когда отдыхаешь на берегу и купаешься в море. Компоненты обычного крема смываются и обесцвечивают кораллы, оставляя рыбок без дома. Накапливаясь в тканях рыб, они препятствуют их размножению и развитию.",
    image: "/img/facts/blueTang-2.jpg",
    second: true,
  },
  octopus: {
    shortTitle: "стирай синтетику в специальном мешке",
    text: "Выбирай вещи из натуральных тканей и волокон — хлопка или льна. Используй специальные мешки для стирки синтетики, лучше стирать при более низких температурах, а также установить специальные фильтры для стиральных машин.",
    image: "/img/facts/octopus-2.jpg",
    second: true,
  },
  whale: {
    shortTitle: "безопасно утилизируй пластик",
    text: "Обязательно сдавай использованные пластиковые бутылки в переработку. Относи их в специальную урну для вторсырья или положи в фандомат для сбора пластиковых бутылок. Помни, большой и чистый океан начинается с наших ежедневных решений и правильных поступков.",
    image: "/img/facts/whale-2.jpg",
    second: true,
  },
}

export function getMarkerFacts(marker: MarkerConfig): [MarkerFact, MarkerFact] {
  return [
    {
      shortTitle: marker.factShortTitle,
      text: marker.factText,
      image: marker.factImage,
      second: marker.second,
    },
    MARKER_SECOND_FACTS[marker.id],
  ]
}

const TURTLE_THANK_YOU_VIDEO_SRC = "/video/turtle/thank-you.mp4"

const TURTLE_THANK_YOU_VIDEO: MarkerVideoAsset = {
  poster: "/video/turtle/thank-you-poster.jpg",
  sources: [{ src: TURTLE_THANK_YOU_VIDEO_SRC, type: "video/mp4" }],
  variants: {
    high: {
      poster: "/video/turtle/thank-you-poster.jpg",
      sources: [{ src: "/video/turtle/thank-you-high.mp4", type: "video/mp4" }],
    },
  },
}

function mascotVideo(folder: "catfish" | "beluga" | "iniya" | "neons" | "stingray" | "blue-tang" | "octopus" | "whale", name: "welcome" | "fact" | "thanks"): MarkerVideoAsset {
  const base = `/video/${folder}/${name}`
  const poster = `${base}-poster.webp`

  return {
    poster,
    sources: [
      { src: `${base}.webm`, type: "video/webm" },
      { src: `${base}.mp4`, type: "video/mp4" },
    ],
    variants: {
      high: {
        poster,
        sources: [
          { src: `${base}.webm`, type: "video/webm" },
          { src: `${base}.mp4`, type: "video/mp4" },
        ],
      },
    },
  }
}

/**
 * markerId — это id станции маршрута. URL-параметр `?marker=` является
 * единым входом для прямого QR-перехода и результата внутреннего QR-сканера.
 * `code` — публичное значение для `?marker=...` в QR и URL.
 * Поле `letter` — буква от слова "ЭКОЛОГИКА", которая выдаётся после
 * прохождения игры этой метки. Поля `fact*` описывают образовательный факт,
 * который показывается в середине каждой игровой сессии и не блокируется тем,
 * что пользователь уже видел его.
 */
export const MARKERS: Record<MarkerId, MarkerConfig> = {
  turtle: {
    id: "turtle",
    name: "Черепаха",
    character: "Тиша",
    greeting:
      "Здравствуй, дружочек! Я — мадам Черепаха. Медлительная на суше, но весьма грациозная в воде. Увы, мы, черепахи, очень рассеянные и часто, видя в воде пластиковый пакет, принимаем его за еду — медуз. Ах, как это печально! Это мешает нырять, останавливает пищеварение, и мы умираем… Избавь нас от пакетов в воде, дружочек!",
    theme: { accent: "#2fe6d0", panel: "rgba(6, 93, 114, 0.82)" },
    code: "turtle",
    gameId: "turtle",
    letter: "О",
    thankYouText:
      "Ах, спасибо тебе, дружочек, от всех черепах! Теперь мы снова можем нырять и есть без опасений. Возьми за это букву-ключ к знаниям об океане.",
    // TODO: добавить ассет /public/mascots/turtle.png — изображение черепахи-маскота для финального попапа.
    thankYouCharacter: "/mascots/turtle.png",
    mascotMini: "/img/mascot-mini/turtle.png",
    hint: {
      mascot: "/img/hint/turtle-hint.png",
      text: "Нажимай на мусор и вылавливай сачком.",
    },
    thankYouVideo: TURTLE_THANK_YOU_VIDEO_SRC,
    videos: {
      welcome: {
        poster: "/video/turtle/welcome-poster.jpg",
        sources: [
          { src: "/video/turtle/welcome.mp4", type: "video/mp4" },
          { src: "/video/turtle/welcome.webm", type: "video/webm" },
        ],
        variants: {
          high: {
            poster: "/video/turtle/welcome-poster.jpg",
            sources: [{ src: "/video/turtle/welcome.mp4", type: "video/mp4" }],
          },
        },
      },
      fact: {
        poster: "/video/turtle/fact-poster.jpg",
        sources: [
          { src: "/video/turtle/fact.mp4", type: "video/mp4" },
          { src: "/video/turtle/fact.webm", type: "video/webm" },
        ],
        variants: {
          high: {
            poster: "/video/turtle/fact-poster.jpg",
            sources: [{ src: "/video/turtle/fact.mp4", type: "video/mp4" }],
          },
        },
      },
      thankYou: TURTLE_THANK_YOU_VIDEO,
    },
    factShortTitle: "в океане есть мусорные острова",
    factText:
      "В океане есть огромные скопления плавающего мусора, называемые «мусорными островами». Они состоят из пластикового мусора, который за счет течений собирается на поверхности в одной зоне. Тихоокеанский «мусорный остров» самый крупный. Его площадь около 1,6 млн квадратных километров. Что почти равно трем Франциям или четырем Германиям.",
    factImage: "/img/facts/turtle.png",
  },
  catfish: {
    id: "catfish",
    name: "Парчовый сом",
    character: "Сом Саныч",
    greeting:
      "Добро пожаловать! Я — парчовый сом, житель речного дна и обладатель завораживающего окраса. К сожалению, тяжелые металлы, пестициды и токсичные вещества в сточных водах, которые смываются с полей и попадают в реки, портят мою кожу и отравляют организм.\n\n" +
      "Помоги очистить воду от ядовитых взвесей, чтобы я снова мог блистать!",
    theme: { accent: "#8fe36d", panel: "rgba(30, 91, 70, 0.84)" },
    code: "catfish",
    gameId: "catfish",
    letter: "О",
    thankYouText:
      "Молодчина! Теперь мне легче дышать, и я снова буду прекрасно выглядеть! Запоминай советы и делись ими с приятелями — так вы станете настоящими друзьями океана и сможете помогать ему в жизни. Для тебя у меня еще одна буква кодового слова.",
    // TODO: добавить ассет /public/mascots/catfish.png — изображение сома-маскота для финального попапа.
    thankYouCharacter: "/mascots/catfish.png",
    mascotMini: "/img/mascot-mini/catfish.png",
    hint: {
      mascot: "/img/hint/catfish-hint.png",
      text: "Наводи телефон на взвесь, удерживай прицел и очищай воду, чтобы убрать химическое загрязнение.",
    },
    thankYouVideo: "/video/catfish/thanks.mp4",
    videos: {
      welcome: mascotVideo("catfish", "welcome"),
      fact: mascotVideo("catfish", "fact"),
      thankYou: mascotVideo("catfish", "thanks"),
    },
    factShortTitle: "Рыбам сложно дышать солью",
    factText:
      "Неорганические загрязнители — минеральные взвеси, соли натрия, кальция и магния — замутняют и засоляют водоемы. Взвесь в воде влияет на жабры рыб, препятствует газообмену, наносит травмы, а также обволакивает икринки сомов и не дает им нормально развиваться.",
    factImage: "/img/facts/catfish-1.jpg",
  },
  beluga: {
    id: "beluga",
    name: "Белуга",
    character: "Белла",
    greeting:
      "Приветствую, мой друг! Я — белуга. Мы, белуги, живем до ста лет и успеваем много повидать в морях и реках. Мы на себе испытали последствия их загрязнения. Разливы нефти, масляная пленка, химия в воде — следствие отношения человека к мировому океану. Мы не можем нормально дышать, питаться и давать потомство. Мы вымираем!\n\n" +
      "Очисти воду, чтобы белуги могли возродить популяцию!",
    theme: { accent: "#9fd7ff", panel: "rgba(48, 85, 135, 0.84)" },
    code: "beluga",
    gameId: "beluga",
    letter: "К",
    thankYouText:
      "Благодарю, мой друг! Запоминай полученные советы и рассказывай друзьям — реальные действия людей дадут белугам больше шансов продолжить свой род. " +
      "Эта буква кодового слова — для тебя. Собери его целиком, обрети мудрость бережного отношения к океану и его обитателям!",
    // TODO: добавить ассет /public/mascots/beluga.png — изображение белуги-маскота для финального попапа.
    thankYouCharacter: "/mascots/beluga.png",
    mascotMini: "/img/mascot-mini/beluga.png",
    hint: {
      mascot: "/img/hint/beluga-hint.png",
      text: "Води пальцем трубу скиммера по экрану и убирай взвесь.",
    },
    thankYouVideo: "/video/beluga/thanks.mp4",
    videos: {
      welcome: mascotVideo("beluga", "welcome"),
      fact: mascotVideo("beluga", "fact"),
      thankYou: mascotVideo("beluga", "thanks"),
    },
    factShortTitle: "нефть — яд для океана",
    factText:
      "Воздействие нефтяных загрязнений особенно губительно для икринок и мальков белуг, как и других осетровых. Оседая на дно, где часто нерестится белуга, нефть препятствует нормальному развитию и снижает выживаемость молоди.",
    factImage: "/img/facts/beluga-1.jpg",
  },
  iniya: {
    id: "iniya",
    name: "Иния",
    character: "Иния",
    greeting:
      "Привет-привет! Я — иния, из вида уникальных розовых пресноводных дельфинов! Я очень нежная и чувствительная. Здоровое питание для моего организма вопрос выживания. И последнее время мне всё сложнее. В моей любимой еде — рыбке — из-за загрязнений химикатами накапливаются токсины. Они травят ее, а она травит меня.\n\n" +
      "Очисти воду, помоги сделать мой рацион безопасным, снова обрести легкость и красоту.",
    theme: { accent: "#d7a6ff", panel: "rgba(83, 55, 130, 0.84)" },
    code: "iniya",
    gameId: "iniya",
    letter: "Л",
    thankYouText:
      "Вау, вот это помощь! Теперь и рыбам легче, и у меня будет больше сил для охоты!\n\n" +
      "Забирай еще букву кодового слова. Следуй советам, береги океан — используй средства с экосоставом.",
    // TODO: добавить ассет /public/mascots/iniya.png — изображение маскота Инии для финального попапа.
    thankYouCharacter: "/mascots/iniya.png",
    mascotMini: "/img/mascot-mini/iniya.png",
    hint: {
      mascot: "/img/hint/iniya-hint.png",
      text: "Прицеливайся и стреляй сорбентами, чтобы убрать химикаты.",
    },
    thankYouVideo: "/video/iniya/thanks.mp4",
    videos: {
      welcome: mascotVideo("iniya", "welcome"),
      fact: mascotVideo("iniya", "fact"),
      thankYou: mascotVideo("iniya", "thanks"),
    },
    factShortTitle: "опасность в сточных водах",
    factText:
      "Из-за добычи золота вдоль рек в воду попадают ртутные загрязнения, отравляющие рыб и дельфинов. Также они страдают из-за использования человеком опасной бытовой химии и ее стоков в водоемы.",
    factImage: "/img/facts/iniya-1.jpg",
  },
  neons: {
    id: "neons",
    name: "Неоны",
    character: "Неоны",
    greeting:
      "Привет-привет, наш защитник! Мы — неоны, речные рыбки, маленькие и ранимые. А еще мы умеем светиться, чтобы находить друг дружку в темноте, спасаясь от хищников. Но есть новая большая опасность — пластик! Этот токсичный и непрошеный гость выделяет вредные химические вещества, которые отравляют нас и портят нашу яркость и красоту.\n\n" +
      "Пожалуйста, очисти воду и помоги нам снова светиться от счастья!",
    theme: { accent: "#b9e8ff", panel: "rgba(52, 83, 108, 0.84)" },
    code: "neons",
    gameId: "neons",
    letter: "Г",
    thankYouText:
      "Спасибо! Мы снова сияем и в безопасности! Помни полезные советы. Следуя им, ты спасаешь наши жизни и жизни других рыбок. Прими от нас еще одну букву кодового слова!",
    // TODO: добавить ассет /public/mascots/neons.png — изображение маскота неонов для финального попапа.
    thankYouCharacter: "/mascots/neons.png",
    mascotMini: "/img/mascot-mini/neons.png",
    hint: {
      mascot: "/img/hint/neons-hint.png",
      text: "Води трубу очистителя пальцем по экрану и удаляй взвесь микропластика.",
    },
    thankYouVideo: "/video/neons/thanks.mp4",
    videos: {
      welcome: mascotVideo("neons", "welcome"),
      fact: mascotVideo("neons", "fact"),
      thankYou: mascotVideo("neons", "thanks"),
    },
    factShortTitle: "пластик крайне токсичен",
    factText:
      "Пластиковые стаканчики, вилки, тарелки и другой мусор при попадании в воду опасны для неонов. В них содержатся токсичные химические вещества, которые выделяются из пластика, особенно под воздействием тепла.",
    factImage: "/img/facts/neons-1.jpg",
  },
  stingray: {
    id: "stingray",
    name: "Скат",
    character: "Скат",
    greeting:
      "Приветик! Я скат. Люблю исследовать дно, добывая любимую еду. Я еще не подрос, поэтому не очень ловкий, и мусор, лежащий на дне, наносит мне раны. А еще я часто принимаю пищевую пленку за вкусняшки, от этого у меня кишечная непроходимость, и я могу погибнуть!\n\n" +
      "Пожалуйста, помоги избавиться от мусора, чтобы мне было безопаснее, и я мог вырасти большим и здоровым!",
    theme: { accent: "#d4eef8", panel: "rgba(43, 75, 96, 0.84)" },
    code: "stingray",
    gameId: "stingray",
    letter: "Э",
    thankYouText:
      "Спасибо тебе! Теперь я могу спокойно плавать, не боясь пораниться и съесть что-то не то. Держи букву кодового слова, разгадав которое ты станешь настоящим другом океана. Ищи другие буквы в других аквариумах.",
    // TODO: добавить ассет /public/mascots/stingray.png — изображение ската-маскота для финального попапа.
    thankYouCharacter: "/mascots/stingray.png",
    mascotMini: "/img/mascot-mini/stringray.png",
    hint: {
      mascot: "/img/hint/stringgray-hint.png",
      text: "Нажимай на мусор и вылавливай сачком.",
    },
    thankYouVideo: "/video/stingray/thanks.mp4",
    videos: {
      welcome: mascotVideo("stingray", "welcome"),
      fact: mascotVideo("stingray", "fact"),
      thankYou: mascotVideo("stingray", "thanks"),
    },
    factShortTitle: "мусор и пластик — враги океана",
    factText:
      "Скаты страдают и погибают не только от самого проглатывания мусора и его механического воздействия, но и от токсичного действия микрочастиц, выделяемых им.",
    factImage: "/img/facts/stingray-1.jpg",
  },
  blueTang: {
    id: "blueTang",
    name: "Рыба Дори",
    character: "Дори",
    greeting:
      "Рад тебя видеть! Я — рыба Дори. Еще меня называют «голубой хирург» из-за острых плавников. Мой дом — это прекрасные коралловые сады, где можно найти пищу, отдохнуть и спрятаться. Однако химические вещества и микропластик в воде обесцвечивают кораллы, нарушают их ДНК и останавливают естественный рост. Кораллы увядают, а вместе с ними — моя семья и другие обитатели рифов.\n\n" +
      "Помоги сохранить мою среду обитания и жизнь. Очищай воду от химии и микропластика.",
    theme: { accent: "#ffb7f7", panel: "rgba(97, 55, 119, 0.84)" },
    code: "blue-tang",
    gameId: "blueTang",
    letter: "И",
    thankYouText:
      "Спасибо! Мой коралловый сад спасен и расцветет вновь! Прими букву кодового слова в благодарность и используй новые знания для помощи океану и его защиты.",
    // TODO: добавить ассет /public/mascots/blue-tang.png — изображение голубого хирурга-маскота для финального попапа.
    thankYouCharacter: "/mascots/blue-tang.png",
    mascotMini: "/img/mascot-mini/blueTang.png",
    hint: {
      mascot: "/img/hint/blueTabg-hint.png",
      text: "Води трубу очистителя пальцем по экрану и убирай микропластик.",
    },
    thankYouVideo: "/video/blue-tang/thanks.mp4",
    videos: {
      welcome: mascotVideo("blue-tang", "welcome"),
      fact: {
        ...mascotVideo("blue-tang", "fact"),
        fitToWidth: true,
        objectFit: "cover",
        objectPosition: "center center",
      },
      thankYou: mascotVideo("blue-tang", "thanks"),
    },
    factShortTitle: "коралловые рифы исчезают",
    factText:
      "Если в воду попадает крем от загара, солнцезащитные очки, упаковка от продуктов — это большая опасность для всех рифовых обитателей! Многие солнцезащитные кремы содержат оксибензон и октиноксат, эти химические вещества наносят вред коралловым рифам и морским обитателям.",
    factImage: "/img/facts/plueTang-1.jpg",
  },
  octopus: {
    id: "octopus",
    name: "Аротрон, осьминог",
    character: "Аротрон",
    greeting:
      "Добрый день… или вечер… Не помню… Ах да, я — осьминог! Мы, осьминоги, сообразительны и любознательны, но в последнее время со мной что-то не так. Ученые говорят, что всему виной загрязнение воды микропластиком. Он может влиять на мою нервную систему и поведение.\n\n" +
      "Очисти воду от микропластика!",
    theme: { accent: "#ffd37a", panel: "rgba(101, 82, 42, 0.84)" },
    code: "octopus",
    gameId: "octopus",
    letter: "К",
    thankYouText:
      "Благодарю! Теперь всё может измениться к лучшему, ведь стало ясно, как бороться с микропластиком и сохранять океан. Возьми еще одну букву кодового слова.",
    // TODO: добавить ассет /public/mascots/octopus.png — изображение осьминога-маскота для финального попапа.
    thankYouCharacter: "/mascots/octopus.png",
    mascotMini: "/img/mascot-mini/octopus.png",
    hint: {
      mascot: "/img/hint/octopus-hint.png",
      text: "Води трубу очистителя пальцем по экрану и убирай взвесь микропластика.",
    },
    thankYouVideo: "/video/octopus/thanks.mp4",
    videos: {
      welcome: mascotVideo("octopus", "welcome"),
      fact: mascotVideo("octopus", "fact"),
      thankYou: mascotVideo("octopus", "thanks"),
    },
    factShortTitle: "одежда выделяет микропластик",
    factText:
      "Один из главных источников микропластика — синтетическая одежда из полиэстера, акрила и нейлона. Сотни тысяч микрочастиц при стирке попадают в водоемы, накапливаются в организмах морских животных и приводят к гибели целых экосистем.",
    factImage: "/img/facts/octopus-1.jpg",
  },
  whale: {
    id: "whale",
    name: "Кит",
    character: "Кит",
    greeting:
      "Рад видеть тебя! Я — кит, самый крупный обитатель океана. Во мне звучит голос его широт и глубин. Всё, чем человек загрязняет океан, проходит через жизни всех его обитателей. Пластик, химия, бытовые и промышленные отходы — всё это обязательно вернется человеку. Если ты здесь, значит твой путь к знаниям о проблемах океана почти завершен. Сделай еще шаг — вылови крупный мусор и не дай мне запутаться в сетях.",
    theme: { accent: "#ff9a72", panel: "rgba(112, 59, 45, 0.84)" },
    code: "whale",
    gameId: "whale",
    letter: "А",
    thankYouText:
      "Спасибо! Теперь ты прошёл экоквест до конца и поговорил с океаном начистоту! Теперь у тебя есть знания, которые помогут беречь океан по-настоящему. Применяй их в повседневной жизни и научи друзей. Держи последнюю букву кодового слова — и помни: океану нужны преданные помощники каждый день! До новых встреч!",
    // TODO: добавить ассет /public/mascots/whale.png — изображение кита-маскота для финального попапа.
    thankYouCharacter: "/mascots/whale.png",
    mascotMini: "/img/mascot-mini/whale.png",
    hint: {
      mascot: "/img/hint/whale-hint.png",
      text: "Нажимай на мусор и вылавливай сачком.",
    },
    thankYouVideo: "/video/whale/thanks.mp4",
    videos: {
      welcome: mascotVideo("whale", "welcome"),
      fact: mascotVideo("whale", "fact"),
      thankYou: mascotVideo("whale", "thanks"),
    },
    factShortTitle: "от 8 000 000 тонн мусора ежедневно",
    factText:
      "Сейчас в океане около 117 млн тонн пластикового и другого мусора. Если не пытаться исправить ситуацию, то к 2040–2050 годам масса мусора превысит массу всей рыбы. И крайне опасный фактор — пластик не растворяется, а распадается на микропластик, что негативно сказывается на всех пищевых цепочках.",
    factImage: "/img/facts/whale-1.jpg",
  },
}

export const FINAL_WORD_MARKER_ORDER: MarkerId[] = [
  "stingray",
  "beluga",
  "catfish",
  "iniya",
  "turtle",
  "neons",
  "blueTang",
  "octopus",
  "whale",
]

export function markerFromUrlHint(marker: string | null): MarkerConfig | null {
  if (!marker) return null

  const normalizedMarker = marker.trim()
  if (normalizedMarker in MARKERS) return MARKERS[normalizedMarker as MarkerId]

  const compactMarker = normalizedMarker.toLowerCase().replace(/[\s_-]+/g, "")
  const matchingMarker = Object.values(MARKERS).find((item) => {
    const compactId = item.id.toLowerCase().replace(/[\s_-]+/g, "")
    const compactCode = item.code.toLowerCase().replace(/[\s_-]+/g, "")
    return compactMarker === compactId || compactMarker === compactCode
  })

  return matchingMarker ?? null
}

export function markerUrlCode(marker: MarkerConfig): string {
  return marker.code
}

export function markerFromSearchParams(
  searchParams: URLSearchParams
): MarkerConfig | null {
  return markerFromUrlHint(searchParams.get("marker"))
}

export function markerFromQrPayload(payload: string): MarkerConfig | null {
  const trimmedPayload = payload.trim()
  if (!trimmedPayload) return null

  try {
    // QR can point to any environment/domain. We intentionally ignore origin
    // and path, and only use the public `marker` query parameter locally.
    const url = new URL(trimmedPayload, "https://mosquarium.local")
    const marker =
      markerFromSearchParams(url.searchParams) ??
      markerFromUrlHint(new URLSearchParams(url.hash.replace(/^#/, "")).get("marker")) ??
      [...url.pathname.split("/").reverse(), url.hash.replace(/^#/, "")]
        .map((part) => markerFromUrlHint(decodeURIComponent(part)))
        .find((item): item is MarkerConfig => Boolean(item)) ??
      null
    if (marker) return marker
  } catch {
    // QR can contain just a marker id/name instead of a full URL.
  }

  return markerFromUrlHint(trimmedPayload)
}
